/**
 * Helpers for reading values off the cookie plugin, whose `get()` is typed as
 * `unknown` (it may return a string, a JSON-parsed object, a number or a
 * boolean). These narrow the raw value at the read site so callers don't have
 * to sprinkle `as` casts.
 */

/**
 * Narrows a raw cookie value to `string | undefined`. Returns `undefined` for
 * anything that isn't a string (missing cookie, parsed object, etc.).
 */
export function asStringCookie (value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

/**
 * Narrows a raw cookie value to `boolean | undefined`. Returns `undefined` for
 * anything that isn't a boolean (missing cookie, string, etc.).
 */
export function asBooleanCookie (value: unknown): boolean | undefined {
  return typeof value === 'boolean' ? value : undefined;
}

/**
 * Narrows a raw cookie value to an object of the caller-supplied shape, or
 * `undefined` when the value isn't a non-null object. The value comes from a
 * JSON-parsed cookie, so its concrete shape can't be verified at runtime — the
 * caller is responsible for choosing a defensive `T` (e.g. a `Partial<...>`).
 */
export function asObjectCookie<T extends object> (value: unknown): T | undefined {
  return typeof value === 'object' && value !== null ? value as T : undefined;
}
